import React, { useState, useEffect, useCallback, useMemo } from "react";
import TopNav from "../../commonUI/TopHeader/TopHeader";
import SearchFilterBar from "../../commonUI/SearchFilterBar/SearchFilterBar";
import DataTable from "../../commonUI/DataTable/DataTable";
import Pagination from "../../commonUI/Pagination/Pagination";
import DateTime from "../../commonUI/DateTime/DateTime";
import { showSpinner, closeSpinner, alert } from "../../../js/modules/Dialogs";
import { sessionExpired } from "../../../config/messages";
import {
  defaultAvatarImage,
  imageFormat,
  serverUrl,
  thumbSuffix,
  userImagesFolder,
} from "../../../config/env";
import { toScreen } from "../../../js/modules/Routing";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Box, Drawer } from "@mui/material";
import { getretrieveTransfers } from "../../../services/transferService";
import { toast } from "react-toastify";
import TransfersDetails from "./TransfersDetails";
import {
  TRANSFER_COLUMNS,
  TRANSFER_SORT_FIELDS,
  TRANSFER_TYPE_OPTIONS,
  TRANSFER_PAYMENT_STATUS,
} from "../../../config/constants";

const formatDateForAPI = (date) => {
  if (!date) return null;
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const Transfers = ({ userDetails }) => {
  const navigate = useNavigate();

  const [drawerOpen,         setDrawerOpen]         = useState(false);
  const [currentView,        setCurrentView]        = useState(null);
  const [selectedTransfer,   setSelectedTransfer]   = useState(null);

  const [searchValue,        setSearchValue]        = useState("");
  const [search,             setSearch]             = useState("");
  const [completedDate,      setCompletedDate]      = useState({ start: null, end: null });
  const [transferTypeFilter, setTransferTypeFilter] = useState("all");
  const [filter,             setFilter]             = useState(
    window.history.state?.usr?.selectedFilter ?? "open"
  );
  const [sort,       setSort]       = useState("");
  const [transfers,  setTransfers]  = useState([]);
  const [pageSize,   setPageSize]   = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [page,       setPage]       = useState(1);
  const [inputValue, setInputValue] = useState(1);

  // ─── Drawer ─────────────────────────────────────────────────────────────────

  const openDrawer = useCallback((view, transfer) => {
    setCurrentView(view);
    setSelectedTransfer(transfer);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  // ─── Data fetching ───────────────────────────────────────────────────────────

  const getTransfers = useCallback(async () => {
    const dateLowerRange = formatDateForAPI(completedDate.start);
    const dateUpperRange = formatDateForAPI(completedDate.end);

    if (
      (completedDate.start && !completedDate.end) ||
      (!completedDate.start && completedDate.end)
    ) {
      return;
    }

    showSpinner();
    try {
      const queryParams = {
        search,
        transfer: transferTypeFilter !== "all" ? transferTypeFilter : undefined,
        dateLowerRange,
        dateUpperRange,
        filter,
        sort,
        page,
        limit: pageSize,
      };
      const resJson = await getretrieveTransfers(queryParams);
      closeSpinner();
      setTotalPages(Math.ceil(resJson?.totalRecords / pageSize));
      setTransfers(resJson?.data || []);
    } catch (error) {
      closeSpinner();
      if (error.response?.status === 401) {
        toast.error(sessionExpired);
        toScreen(false, navigate, "/logout");
      } else {
        setTransfers([]);
        alert(error.message || "Failed to fetch transfers");
      }
    }
  }, [search, transferTypeFilter, completedDate, filter, sort, page, pageSize, navigate]);

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleSearch = useCallback(() => {
    setSearch(searchValue);
    setFilter(searchValue === "" ? "open" : null);
    setPage(1);
  }, [searchValue]);

  const handleReset = () => {
    setSort("");
    setFilter("");
    setTransferTypeFilter("all");
    setCompletedDate({ start: null, end: null });
    setSearchValue("");
    setSearch("");
    setPage(1);
  };

  const sortTransfers = useCallback(
    (event, field) => {
      if (event) event.preventDefault();
      const currentSort = TRANSFER_SORT_FIELDS[field];
      if (!currentSort) return;
      const newSort = sort === currentSort.asc ? currentSort.desc : currentSort.asc;
      setSearch("");
      setSort(newSort);
      setPage(1);
    },
    [sort]
  );

  const getSortClass = useCallback(
    (key) => {
      if (sort === `${key}=1`)  return "sort-button sorting-icon sorting-icon-up";
      if (sort === `${key}=-1`) return "sort-button sorting-icon sorting-icon-down";
      return "sort-button";
    },
    [sort]
  );

  // ─── Avatar ──────────────────────────────────────────────────────────────────

  const getAvatarSrc = useCallback((transfer) => {
    const driver = transfer?.driverDetails;
    if (driver?.avatar && driver.avatar !== "") {
      return `${serverUrl}${userImagesFolder}/${driver.shortId}/${driver.avatar}${thumbSuffix}${imageFormat}`;
    }
    return `../img/${defaultAvatarImage}${imageFormat}`;
  }, []);

  // ─── Row renderer ────────────────────────────────────────────────────────────

  const renderRow = useCallback(
    (transfer, index) => {
      const paymentStatus =
        transfer?.jobDetails?.mavPaymentDetails?.paymentTransfer;
      const { badgeClass, label } =
        TRANSFER_PAYMENT_STATUS[paymentStatus] || {
          badgeClass: "badge-secondary",
          label: "-",
        };

      return (
        <tr key={index}>
          <td>
            <div className="d-flex align-items-center g-12">
              <img
                src={getAvatarSrc(transfer)}
                alt="avatar"
                className="avatar-pic"
                onError={(e) => { e.target.src = defaultAvatarImage; }}
              />
              <div className="text-capitalize">
                {transfer?.driverDetails?.firstname || "-"}
                <br />
                <span className="text-sm text-gray-600">
                  {transfer?.driverDetails?.shortId}
                </span>
              </div>
            </div>
          </td>

          <td>
            <span className={`badge text-capitalize ${badgeClass}`}>
              {label}
            </span>
          </td>

          <td>£{transfer?.jobDetails?.bookedFor / 100 || "0"}</td>

          <td className="text-capitalize">{transfer?.jobDetails?.jobType}</td>

          <td>
            <span className="text-capitalize">
              <div>{transfer?.jobDetails?.from || ""}</div>
              {(transfer?.jobDetails?.from || transfer?.jobDetails?.to) && (
                <div className="text-sm text-gray-600">
                  {transfer?.jobDetails?.to ? "→ " : ""}
                  {transfer?.jobDetails?.to || ""}
                </div>
              )}
            </span>
          </td>

          <td>
            <DateTime date={transfer?.createdAt} />
          </td>

          <td>
            <button
              className="custom-btn"
              onClick={() => openDrawer("edit", transfer)}
            >
              Review
            </button>
          </td>
        </tr>
      );
    },
    [getAvatarSrc, openDrawer]
  );

  // ─── Filters config ──────────────────────────────────────────────────────────

  const filters = useMemo(
    () => [
      {
        type:        "search",
        key:         "search",
        label:       "Search",
        value:       searchValue,
        onChange:    setSearchValue,
        placeholder: "Search...",
        onSubmit:    handleSearch,
      },
      {
        type:        "select",
        key:         "transfer",
        label:       "Transfer",
        value:       transferTypeFilter,
        onChange:    setTransferTypeFilter,
        placeholder: "Select Transfer",
        options:     TRANSFER_TYPE_OPTIONS,
      },
      {
        type:        "dateRange",
        key:         "completed",
        label:       "Completed",
        value:       completedDate,
        onChange:    setCompletedDate,
        placeholder: "Anytime",
      },
    ],
    [searchValue, transferTypeFilter, completedDate, handleSearch]
  );

  // ─── Effects ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    setInputValue(page);
  }, [page]);

  useEffect(() => {
    getTransfers();
  }, [filter, page, pageSize, sort, search, transferTypeFilter, completedDate]);

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <TopNav title="Transfers" />
      <div className="global-body">
        <div className="global-body-container">
          <SearchFilterBar filters={filters} onReset={handleReset} />

          <DataTable
            data={transfers}
            columns={TRANSFER_COLUMNS}
            renderRow={renderRow}
            onSort={sortTransfers}
            getSortClass={getSortClass}
            emptyMessage="No transfers found"
            tableClassName="table clientTable transfersTable"
            colSpan={7}
          />

          <Pagination
            pageSize={pageSize}
            totalPages={totalPages}
            page={page}
            setPage={setPage}
            inputValue={inputValue}
            setInputValue={setInputValue}
            setPageSize={setPageSize}
          />
        </div>
      </div>

      {/* Single Drawer rendered once at component level */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        ModalProps={{
          BackdropProps: { style: { backgroundColor: "rgba(0, 0, 0, 0.1)" } },
        }}
        sx={{ boxShadow: "none", "& .MuiDrawer-paper": { boxShadow: "none" } }}
      >
        <Box
          sx={{ width: 600 }}
          role="presentation"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TransfersDetails
            view={currentView}
            onClose={closeDrawer}
            selectedTransfer={selectedTransfer}
            userDetails={userDetails}
            theCallBackFunc={getTransfers}
          />
        </Box>
      </Drawer>
    </>
  );
};

Transfers.propTypes = {
  userDetails: PropTypes.object.isRequired,
};

export default Transfers;
